import { ProgramVisitBadge as DesignSystemProgramVisitBadge } from '@quickmd-llc/design-system'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import { selectProgramVisitBadgeFlag } from '@/store/flags/flagsSelectors'

type Props = {
  className?: string
  /**
   * The visit's `Coverage` block. `unknown` because every field on the generated
   * contract is optional and the patient chart's visit lists arrive through
   * untyped Redux — the design-system badge parses it, and that parse is the
   * render gate.
   */
  coverage: unknown
}

/**
 * `Quit 7-OH Plan` — the chip a support-plan visit wears wherever staff see that
 * visit: the patient chart's visit cards and the visit-info patient card. The
 * label carried a `Visit N of M` suffix until QMD-4439 dropped it — admins now
 * edit remaining visits in the plan panel, so a per-visit count no longer
 * reliably reflects the plan.
 *
 * The chip itself is `@quickmd-llc/design-system`'s, because admin, provider and
 * the patient app all draw the same one. What this wrapper adds is the kill
 * switch, which reads this app's Redux and so cannot live in a shared package.
 * Reading it here rather than at each placement means turning
 * `program_visit_badge_admin` off removes every chip in the app instead of the
 * subset whoever added the newest surface remembered to gate.
 *
 * Beyond the kill switch this wrapper decides nothing: it hands `coverage`
 * straight to the design-system chip, which parses it and is the render gate.
 * That chip renders nothing when the visit was not covered or the plan has no
 * name (the name is now the chip's entire content), so callers can drop this in
 * unconditionally instead of repeating the guard.
 */
export function ProgramVisitBadge({ className, coverage }: Props) {
  const isProgramVisitBadgeEnabled = useAppSelector(selectProgramVisitBadgeFlag)

  if (!isProgramVisitBadgeEnabled) {
    return null
  }

  return <DesignSystemProgramVisitBadge className={className} coverage={coverage} />
}
